if (!customElements.get('giveaway-exit-modal')) {
  customElements.define(
    'giveaway-exit-modal',
    class GiveawayExitModal extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this.dialog = this.querySelector('.giveaway-exit-modal');
        this.card = this.querySelector('.giveaway-exit-modal__card');
        this.closeButton = this.querySelector('[data-exit-modal-close]');
        this.primaryButton = this.querySelector('[data-exit-modal-primary]');
        this.secondaryButton = this.querySelector('[data-exit-modal-secondary]');
        this.errorEl = this.querySelector('[data-exit-modal-error]');
        this.spinner = this.querySelector('[data-exit-modal-spinner]');

        this.desktopOnly = this.dataset.desktopOnly === 'true';
        this.showOncePerSession = this.dataset.showOncePerSession === 'true';
        this.storageKey = this.dataset.storageKey || 'giveawayExitModalShown';

        this.scrollPosition = window.scrollY || 0;
        this.exitIntentBound = false;
        this._isClosing = false;
        this._closeTimer = null;

        this.bindEvents();

        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          this.handleDesignMode();
        }

        if (this.hasBeenShown()) return;

        if (this.desktopOnly && !this.isDesktop()) return;

        this.bindExitIntent();
      }

      disconnectedCallback() {
        this.unbindExitIntent();
      }
      
      isDesktop() {
        return window.matchMedia('(min-width: 990px)').matches;
      }

      hasBeenShown() {
        if (!this.showOncePerSession) return false;
        return sessionStorage.getItem(this.storageKey) === 'true';
      }

      markShown() {
        if (!this.showOncePerSession) return;
        sessionStorage.setItem(this.storageKey, 'true');
      }

      bindEvents() {
        this.dialog?.addEventListener('click', (event) => this.handleDialogClick(event));
        this.closeButton?.addEventListener('click', () => this.closeModal());
        this.secondaryButton?.addEventListener('click', () => {
          this.closeModal();
          const offer = document.getElementById('giveaway-offer');
          offer?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        this.primaryButton?.addEventListener('click', (event) => this.handlePrimaryClick(event));
        document.addEventListener('keydown', this.handleKeydown);
        this.dialog?.addEventListener('close', () => {
          this.dialog?.classList.remove('is-active', 'is-closing');
          this._isClosing = false;
          if (this._closeTimer) {
            clearTimeout(this._closeTimer);
            this._closeTimer = null;
          }
          this.bodyLock(false);
        });
      }

      bindExitIntent() {
        if (this.exitIntentBound) return;

        this.onMouseLeave = (event) => {
          if (this.desktopOnly && !this.isDesktop()) return;
          if (this.dialog?.open) return;
          if (event.clientY > 0) return;

          this.unbindExitIntent();
          this.openModal();
        };

        document.documentElement.addEventListener('mouseleave', this.onMouseLeave);
        this.exitIntentBound = true;
      }

      unbindExitIntent() {
        if (!this.exitIntentBound) return;

        document.documentElement.removeEventListener('mouseleave', this.onMouseLeave);
        this.exitIntentBound = false;
      }

      handleDesignMode() {
        document.addEventListener('shopify:section:select', (event) => {
          if (event.target.contains(this)) {
            this.openModal();
          }
        });

        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.target.contains(this)) {
            this.closeModal();
          }
        });
      }

      handleDialogClick(event) {
        if (event.target === this.dialog) {
          this.closeModal();
        }
      }

      handleKeydown = (event) => {
        if (!this.dialog?.open) return;
        if (event.key === 'Escape' || event.key === 'Esc') {
          this.closeModal();
        }
      };

      openModal() {
        if (!this.dialog || this.dialog.open) return;

        this.scrollPosition = window.scrollY || 0;
        this.dialog.showModal();
        this.bodyLock(true);

        requestAnimationFrame(() => {
          this.dialog?.classList.add('is-active');
        });

        if (!(typeof Shopify !== 'undefined' && Shopify.designMode)) {
          this.markShown();
        }
      }

      closeModal() {
        if (!this.dialog?.open || this._isClosing) return;

        this._isClosing = true;
        this.dialog.classList.remove('is-active');
        this.dialog.classList.add('is-closing');

        const finishClose = () => {
          if (!this._isClosing || !this.dialog?.open) return;

          this._isClosing = false;

          if (this._closeTimer) {
            clearTimeout(this._closeTimer);
            this._closeTimer = null;
          }

          this.dialog.classList.remove('is-closing');
          this.dialog.close();

          if (typeof Shopify !== 'undefined' && Shopify.designMode) return;
          if (this.showOncePerSession && this.hasBeenShown()) return;

          this.bindExitIntent();
        };

        this._closeTimer = window.setTimeout(finishClose, 420);

        if (this.card) {
          this.card.addEventListener(
            'transitionend',
            (event) => {
              if (event.target !== this.card || event.propertyName !== 'transform') return;
              finishClose();
            },
            { once: true }
          );
        }
      }

      bodyLock(value) {
        const body = document.body;

        if (value) {
          body.style.top = `-${this.scrollPosition}px`;
          body.style.overflow = 'hidden';
          body.style.position = 'fixed';
          body.style.width = '100%';
          return;
        }

        body.style.removeProperty('overflow');
        body.style.removeProperty('position');
        body.style.removeProperty('width');
        body.style.removeProperty('top');
        window.scrollTo(0, this.scrollPosition);
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = !message;
      }

      setLoading(isLoading) {
        if (!this.primaryButton) return;

        if (isLoading) {
          this.primaryButton.setAttribute('aria-disabled', 'true');
          this.primaryButton.classList.add('loading');
          this.spinner?.classList.remove('hidden');
        } else {
          this.primaryButton.classList.remove('loading');
          this.primaryButton.removeAttribute('aria-disabled');
          this.spinner?.classList.add('hidden');
        }
      }

      getCartAddUrl() {
        return (window.routes && window.routes.cart_add_url) || '/cart/add.js';
      }

      getCartClearUrl() {
        return '/cart/clear.js';
      }

      buildItems() {
        const variantId = this.primaryButton?.dataset.variantId;
        const sellingPlan = this.primaryButton?.dataset.sellingPlanId;
        const quantity = parseInt(this.primaryButton?.dataset.quantity, 10) || 1;

        if (!variantId || !sellingPlan) return null;

        return [{ id: String(variantId), quantity, selling_plan: sellingPlan }];
      }

      async handlePrimaryClick(event) {
        event.preventDefault();
        this.showError('');

        const items = this.buildItems();
        if (!items) {
          this.showError('This offer is not configured yet.');
          return;
        }

        this.setLoading(true);

        try {
          await fetch(this.getCartClearUrl(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
          });

          const response = await fetch(this.getCartAddUrl(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items }),
          });

          const data = await response.json();
          if (!response.ok || data.status) {
            const message = data.description || data.message || 'Could not start checkout.';
            this.showError(message);
            return;
          }

          window.location.href = '/checkout';
        } catch (error) {
          console.error('[giveaway-exit-modal]', error);
          this.showError('Network error. Please try again.');
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}
